// Währungen der Belege (wie server/src/modules/rechnungstool/waehrung.ts, ein Test vergleicht beide): Namen, Kürzel und
// ob die EZB einen Referenzkurs herausgibt. Kurs am Beleg: Euro je 1 Einheit, als Text mit bis zu 10 Nachkommastellen
// (wie der Server ihn speichert). Ohne React, damit der Server-Test die Datei lesen kann.

export type Waehrung = 'EUR' | 'USD' | 'CHF' | 'GBP' | 'AED' | 'PLN' | 'CZK' | 'DKK' | 'SEK' | 'NOK' | 'TRY' | 'CAD' | 'AUD' | 'CNY';

export const WAEHRUNGEN: Record<Waehrung, { name: string; ezb: boolean }> = {
  EUR: { name: 'Euro', ezb: false },
  USD: { name: 'US-Dollar', ezb: true },
  CHF: { name: 'Schweizer Franken', ezb: true },
  GBP: { name: 'Britisches Pfund', ezb: true },
  AED: { name: 'VAE-Dirham', ezb: false },
  PLN: { name: 'Polnischer Złoty', ezb: true },
  CZK: { name: 'Tschechische Krone', ezb: true },
  DKK: { name: 'Dänische Krone', ezb: true },
  SEK: { name: 'Schwedische Krone', ezb: true },
  NOK: { name: 'Norwegische Krone', ezb: true },
  TRY: { name: 'Türkische Lira', ezb: true },
  CAD: { name: 'Kanadischer Dollar', ezb: true },
  AUD: { name: 'Australischer Dollar', ezb: true },
  CNY: { name: 'Chinesischer Yuan', ezb: true },
};
export const WAEHRUNG_CODES = Object.keys(WAEHRUNGEN) as Waehrung[];
export const istWaehrung = (w: unknown): w is Waehrung => typeof w === 'string' && w in WAEHRUNGEN;
// unbekannt oder fehlt (ältere Stände): Euro
export const waehrungAus = (w: unknown): Waehrung => (istWaehrung(w) ? w : 'EUR');

// Einheit hinter dem Betrag: Euro als „€“, sonst das Kürzel (CAD, AUD und USD sind alle „$“)
export const einheit = (w: string = 'EUR') => (w === 'EUR' ? '€' : w);
// „US-Dollar (USD)“
export const waehrungName = (w: string) => `${WAEHRUNGEN[w as Waehrung]?.name ?? w} (${w})`;

// Kurs in Zehnmilliardstel als ganze Zahl (BigInt), gerechnet wie auf dem Server
const SKALA = 10n ** 10n;
const skaliert = (k: string): bigint => {
  const [int = '0', frac = ''] = k.split('.');
  return BigInt(int) * SKALA + BigInt((frac + '0'.repeat(10)).slice(0, 10));
};
const text10 = (n: bigint) => `${n / SKALA}.${String(n % SKALA).padStart(10, '0')}`;
// a / b kaufmännisch gerundet (halb vom Nullpunkt weg), b > 0
const teile = (a: bigint, b: bigint) => {
  const neg = a < 0n;
  const x = neg ? -a : a;
  const q = (2n * x + b) / (2n * b);
  return neg ? -q : q;
};

// Kurs aus dem Feld: „0,919456“ oder „1.0876“ → '0.9194560000'; null: kein gültiger Kurs (≤ 0, über 1 Million, mehr
// als 10 Nachkommastellen, keine Zahl)
export function kursNorm(t: string | number | null | undefined): string | null {
  if (t === null || t === undefined) return null;
  let s = typeof t === 'number' ? (Number.isFinite(t) ? (/e/i.test(String(t)) ? t.toFixed(10) : String(t)) : '') : t.trim();
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  const m = /^(\d+)(?:\.(\d+))?$/.exec(s);
  if (!m) return null;
  const frac = (m[2] ?? '').replace(/0+$/, '');
  if (frac.length > 10) return null;
  const n = skaliert(`${m[1]}.${frac}`);
  if (n <= 0n || n > 1_000_000n * SKALA) return null;
  return text10(n);
}

// Kehrwert auf 10 Stellen: Euro je Einheit ↔ Einheiten je Euro (so gibt die EZB den Kurs an)
export const kehrwert = (k: string): string => text10(teile(SKALA * SKALA, skaliert(k)));

// Dezimalzahl mit Komma, gerundet auf höchstens max Stellen, Nullen am Ende weg, mindestens min Stellen
export function dez(k: string, max: number, min = 2): string {
  const n = teile(skaliert(k), 10n ** BigInt(10 - max));
  const p = 10n ** BigInt(max);
  const frac = String(n % p).padStart(max, '0').replace(/0+$/, '').padEnd(min, '0');
  return frac ? `${n / p},${frac}` : String(n / p);
}

// Kurs fürs Feld (6 Stellen, ohne Nullen am Ende): '0.9194556822' → '0,919456'
export const kursFeld = (k: string) => dez(k, 6, 0);
// „1 USD = 0,919456 € (1 € = 1,0876 USD)“
export const kursText = (k: string, w: string) => `1 ${w} = ${dez(k, 6)} € (1 € = ${dez(kehrwert(k), 6, 4)} ${w})`;

// Cent der Belegwährung in Euro-Cent zum Kurs, genau wie auf dem Server (Umsatzsteuer in Euro je Steuerart)
export const inEur = (cents: number, kurs: string): number => Number(teile(BigInt(cents) * skaliert(kurs), SKALA)) + 0;
// Euro-Cent in Cent der Belegwährung, genau wie auf dem Server (Mahngebühr, Verzugspauschale); kurs als Zahl oder Text
export const ausEur = (eurCents: number, kurs: string | number): number => {
  const k = kursNorm(kurs);
  return k ? Number(teile(BigInt(eurCents) * SKALA, skaliert(k))) + 0 : eurCents;
};

// Betrag der Belegwährung (in Einheiten, z. B. 1190.5) in Euro zum Kurs, auf Cent (für Kennzahlen)
export const inEurZahl = (v: number, kurs: string | number) => Math.round(v * 100 * Number(kurs)) / 100;
// Preis aus „Meine Leistungen“ (Euro) in der Belegwährung, 4 Nachkommastellen
export const preisAusEur = (preis: number, kurs: string | number) => Math.round((preis / Number(kurs)) * 10000) / 10000;

// weicht der Kurs um mehr als 10 % vom EZB-Kurs ab? (Hinweis, keine Sperre)
export const weichtAb = (kurs: string, ezbKurs: string) => Math.abs(Number(kurs) / Number(ezbKurs) - 1) > 0.1;
